import { useState } from 'react';

export default function SalesReport() {
    const [dateFrom, setDateFrom] = useState('');
    const [dateTo, setDateTo] = useState('');
    const [rows, setRows] = useState([]);
    const [totals, setTotals] = useState(null);
    const [loading, setLoading] = useState(false);
    const [file, setFile] = useState(null);
    const [message, setMessage] = useState('');
    const [messageType, setMessageType] = useState('');

    const fetchReport = async () => {
        setLoading(true);
        setMessage('');
        try {
            const params = new URLSearchParams();
            if (dateFrom) params.append('date_from', dateFrom);
            if (dateTo) params.append('date_to', dateTo);

            const resp = await fetch('/api/sales/report?' + params.toString(), { credentials: 'same-origin' });
            const data = await resp.json();
            setRows(data.rows || []);
            setTotals(data.totals || null);
        } catch (err) {
            console.error(err);
            setMessage('Erreur lors de la récupération du rapport');
            setMessageType('error');
        } finally {
            setLoading(false);
        }
    };

    const handleImport = async () => {
        if (!file) {
            setMessage('Sélectionnez un fichier CSV');
            setMessageType('error');
            return;
        }
        setLoading(true);
        setMessage('');
        try {
            const fd = new FormData();
            fd.append('file', file);

            const resp = await fetch('/api/import/sales', {
                method: 'POST',
                credentials: 'same-origin',
                headers: {
                    'X-CSRF-TOKEN': document.querySelector('meta[name="csrf-token"]')?.content || '',
                },
                body: fd,
            });
            const data = await resp.json();
            if (!resp.ok) throw new Error(data.error || data.message || 'Import failed');
            setMessage(`Import réussi : ${data.created} ligne(s) ajoutée(s)`);
            setMessageType('success');
            setFile(null);
            fetchReport();
        } catch (err) {
            console.error(err);
            setMessage(err.message || "Erreur lors de l'import");
            setMessageType('error');
        } finally {
            setLoading(false);
        }
    };

    const handleReset = () => {
        setDateFrom('');
        setDateTo('');
        setRows([]);
        setTotals(null);
        setMessage('');
    };

    const rowTotal = (row) => Number(row.avg_price) * Number(row.quantity_sold);
    const totalLineAmount = rows.reduce((sum, row) => sum + rowTotal(row), 0);

    const marginRate = (row) => {
        const revenue = rowTotal(row);
        if (!revenue) return 0;
        return ((Number(row.total_margin) / revenue) * 100).toFixed(1);
    };

    return (
        <div className="space-y-5 p-1">

            {/* Header */}
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-xl font-semibold text-gray-900">Rapport des ventes</h1>
                    <p className="text-sm text-gray-500 mt-0.5">Suivi du chiffre d'affaires et des marges</p>
                </div>

                {/* Import CSV */}
                <div className="flex items-center gap-2">
                    <label className="flex items-center gap-2 cursor-pointer rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-600 hover:bg-gray-50 transition-colors">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
                        </svg>
                        {file ? file.name : 'Choisir un fichier CSV'}
                        <input type="file" accept=".csv" className="hidden" onChange={(e) => setFile(e.target.files?.[0] ?? null)} />
                    </label>
                    <button
                        onClick={handleImport}
                        disabled={loading || !file}
                        className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                        {loading ? 'Import...' : 'Importer'}
                    </button>
                </div>
            </div>

            {/* Message feedback */}
            {message && (
                <div className={`rounded-lg px-4 py-3 text-sm flex items-center gap-2 ${messageType === 'success' ? 'bg-green-50 text-green-800 border border-green-200' : 'bg-red-50 text-red-800 border border-red-200'}`}>
                    {messageType === 'success' ? (
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                    ) : (
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                    )}
                    {message}
                </div>
            )}

            {/* Metric cards */}
            {totals && (
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                    <div className="rounded-xl bg-gray-50 p-4">
                        <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Produits</p>
                        <p className="text-2xl font-semibold text-gray-900">{rows.length}</p>
                    </div>
                    <div className="rounded-xl bg-gray-50 p-4">
                        <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Qté totale</p>
                        <p className="text-2xl font-semibold text-gray-900">{Number(totals.total_quantity).toLocaleString()}</p>
                    </div>
                    <div className="rounded-xl bg-green-50 p-4">
                        <p className="text-xs text-green-700 uppercase tracking-wide mb-1">Marge totale</p>
                        <p className="text-2xl font-semibold text-green-800">Ar {Number(totals.total_margin).toLocaleString()}</p>
                    </div>
                    <div className="rounded-xl bg-blue-50 p-4">
                        <p className="text-xs text-blue-700 uppercase tracking-wide mb-1">Chiffre d'affaires</p>
                        <p className="text-2xl font-semibold text-blue-800">Ar {Number(totals.total_revenue).toLocaleString()}</p>
                    </div>
                </div>
            )}

            {/* Table card */}
            <div className="rounded-xl border border-gray-200 bg-white overflow-hidden">

                {/* Filters */}
                <div className="flex flex-wrap items-center gap-3 px-5 py-3 border-b border-gray-100 bg-gray-50/60">
                    <div className="flex items-center gap-2 bg-white rounded-lg border border-gray-200 px-3 py-1.5">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                        </svg>
                        <span className="text-xs text-gray-400">Du</span>
                        <input
                            type="date"
                            value={dateFrom}
                            onChange={(e) => setDateFrom(e.target.value)}
                            className="text-sm text-gray-700 border-none outline-none bg-transparent"
                        />
                    </div>

                    <div className="flex items-center gap-2 bg-white rounded-lg border border-gray-200 px-3 py-1.5">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                        </svg>
                        <span className="text-xs text-gray-400">Au</span>
                        <input
                            type="date"
                            value={dateTo}
                            onChange={(e) => setDateTo(e.target.value)}
                            className="text-sm text-gray-700 border-none outline-none bg-transparent"
                        />
                    </div>

                    <button
                        onClick={fetchReport}
                        disabled={loading}
                        className="rounded-lg bg-green-600 px-4 py-1.5 text-sm font-medium text-white hover:bg-green-700 disabled:opacity-50 transition-colors"
                    >
                        {loading ? 'Chargement...' : 'Filtrer'}
                    </button>

                    <button
                        onClick={handleReset}
                        className="ml-auto rounded-lg border border-gray-200 px-3 py-1.5 text-sm text-gray-500 hover:bg-gray-100 transition-colors"
                    >
                        Réinitialiser
                    </button>
                </div>

                {/* Table */}
                <div className="overflow-x-auto">
                    <table className="min-w-full text-sm">
                        <thead>
                            <tr className="border-b border-gray-100">
                                <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wide text-gray-400">Date</th>
                                <th className="px-5 py-3 text-left text-xs font-medium uppercase tracking-wide text-gray-400">Produit</th>
                                <th className="px-4 py-3 text-right text-xs font-medium uppercase tracking-wide text-gray-400">Qté vendue</th>
                                <th className="px-4 py-3 text-right text-xs font-medium uppercase tracking-wide text-gray-400">Prix vente</th>
                                <th className="px-4 py-3 text-right text-xs font-medium uppercase tracking-wide text-gray-400">Total ligne</th>
                                <th className="px-4 py-3 text-right text-xs font-medium uppercase tracking-wide text-gray-400">Coût unité</th>
                                <th className="px-4 py-3 text-right text-xs font-medium uppercase tracking-wide text-gray-400">Marge</th>
                                <th className="px-4 py-3 text-right text-xs font-medium uppercase tracking-wide text-gray-400">Taux</th>
                                <th className="px-4 py-3 text-right text-xs font-medium uppercase tracking-wide text-gray-400">CA</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-50">
                            {rows.length === 0 ? (
                                <tr>
                                    <td colSpan={8} className="px-5 py-10 text-center text-sm text-gray-400">
                                        Aucune donnée — sélectionnez une période et cliquez sur Filtrer.
                                    </td>
                                </tr>
                            ) : (
                                rows.map((r) => (
                                    <tr key={`${r.dish_id}-${r.sold_date}`} className="hover:bg-gray-50/60 transition-colors">
                                        <td className="px-5 py-3 text-left text-gray-700">{new Date(r.sold_date).toLocaleDateString()}</td>
                                        <td className="px-5 py-3 font-medium text-gray-900">{r.product}</td>
                                        <td className="px-4 py-3 text-right text-gray-700">{Number(r.quantity_sold).toLocaleString()}</td>
                                        <td className="px-4 py-3 text-right text-gray-500">Ar {Number(r.avg_price).toLocaleString()}</td>
                                        <td className="px-4 py-3 text-right text-gray-500">Ar {Number(rowTotal(r)).toLocaleString()}</td>
                                        <td className="px-4 py-3 text-right text-gray-500">Ar {Number(r.cost_per_unit).toLocaleString()}</td>
                                        <td className="px-4 py-3 text-right text-green-700 font-medium">Ar {Number(r.total_margin).toLocaleString()}</td>
                                        <td className="px-4 py-3 text-right">
                                            <span className="inline-flex items-center rounded-full bg-green-50 px-2 py-0.5 text-xs font-medium text-green-700">
                                                {marginRate(r)} %
                                            </span>
                                        </td>
                                        <td className="px-4 py-3 text-right font-semibold text-gray-900">Ar {Number(r.revenue).toLocaleString()}</td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                        {totals && (
                            <tfoot>
                                <tr className="border-t border-gray-200 bg-gray-50/80">
                                    <td className="px-5 py-3" />
                                    <td className="px-5 py-3 font-semibold text-gray-700">Totaux</td>
                                    <td className="px-4 py-3 text-right font-semibold text-gray-700">{Number(totals.total_quantity).toLocaleString()}</td>
                                    <td className="px-4 py-3" />
                                    <td className="px-4 py-3 text-right font-semibold text-gray-700">Ar {Number(totalLineAmount).toLocaleString()}</td>
                                    <td className="px-4 py-3" />
                                    <td className="px-4 py-3 text-right font-semibold text-green-700">Ar {Number(totals.total_margin).toLocaleString()}</td>
                                    <td className="px-4 py-3" />
                                    <td className="px-4 py-3" />
                                    <td className="px-4 py-3 text-right font-semibold text-gray-900">Ar {Number(totals.total_revenue).toLocaleString()}</td>
                                </tr>
                            </tfoot>
                        )}
                    </table>
                </div>

                {/* Footer */}
                <div className="flex items-center justify-between px-5 py-3 border-t border-gray-100 text-xs text-gray-400">
                    <span>{rows.length} produit(s) affiché(s)</span>
                    {rows.length > 0 && (
                        <button className="hover:text-gray-600 transition-colors flex items-center gap-1">
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                            </svg>
                            Exporter
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
}